/**
 * Tech Request Hub - fixed configuration (spec: work-system/spec.md).
 *
 * Divisions, the org tree, statuses and types live here in code on purpose
 * (Baker, 2026-09-24: only people/roles and capacity/scoring are editable in
 * Admin). firestore.rules repeats the id lists; tests/config.test.ts keeps the
 * two in sync.
 */

/**
 * The permanent OWNER (Baker Knight; Firebase Auth uid, provider microsoft.com).
 * Always an editor with Collections access, never removable. firestore.rules
 * (ownerUid) and functions/src/owner.ts hold the same uid; tests/config.test.ts
 * keeps them in sync.
 */
export const OWNER_UID = 'J0SOONI9gdfvdbCaeT1cCwvmrOm1';

export type DivisionId =
  | 'all' | 'executive' | 'hr' | 'sales' | 'preproduction' | 'production'
  | 'repair' | 'gutters' | 'qc' | 'finance' | 'marketing' | 'technology';

export interface Division {
  id: DivisionId;
  label: string;
  leader?: string;
  /** Parent node in the org tree (a division id or an intermediate node id). */
  reportsTo?: string;
}

/** 'all' = Company-wide; rendered as the band across the top of the org board. */
export const DIVISIONS: Division[] = [
  { id: 'all', label: 'Company-wide' },
  { id: 'executive', label: 'Executive', leader: 'Robin Scherer' },
  { id: 'sales', label: 'Sales', leader: 'Chad Perdue', reportsTo: 'coo' },
  { id: 'preproduction', label: 'Pre-Production', leader: 'Andrew Magnie', reportsTo: 'coo' },
  { id: 'production', label: 'Production', leader: 'Olivia Strickling', reportsTo: 'coo' },
  { id: 'repair', label: 'Repair / Service', leader: 'Jason Tremblay', reportsTo: 'production' },
  { id: 'qc', label: 'Quality Control', leader: 'Bryce Reynolds', reportsTo: 'production' },
  { id: 'gutters', label: 'Gutters', leader: 'Hartford Haffenreffer', reportsTo: 'coo' },
  { id: 'finance', label: 'Finance', leader: 'Renee Parker', reportsTo: 'cfo' },
  { id: 'hr', label: 'HR', leader: "Brendan O'Firinn", reportsTo: 'executive' },
  { id: 'marketing', label: 'Marketing', leader: 'Jeffery Newsome', reportsTo: 'executive' },
  { id: 'technology', label: 'Technology', leader: 'Baker Knight', reportsTo: 'executive' },
];

/** Intermediate org-tree nodes that are people, not divisions. */
export const ORG_NODES = [
  { id: 'coo', label: 'COO', leader: 'Nick Munoz', reportsTo: 'executive' },
  { id: 'cfo', label: 'CFO', leader: 'Prasad Tare', reportsTo: 'executive' },
];

export const DIVISION_IDS = DIVISIONS.map((d) => d.id);
export const divisionLabel = (id: string) => DIVISIONS.find((d) => d.id === id)?.label ?? id;

export type RequestType = 'support' | 'bug' | 'feature' | 'project' | 'access';
export const TYPES: { id: RequestType; label: string; plain: string }[] = [
  { id: 'support', label: 'Support', plain: 'I need help with something' },
  { id: 'bug', label: 'Bug', plain: "Something's broken" },
  { id: 'access', label: 'Admin / Access', plain: 'I need access or setup' },
  { id: 'feature', label: 'Feature request', plain: 'New feature or report' },
  { id: 'project', label: 'Project', plain: 'Bigger project idea' },
];
export const TYPE_IDS = TYPES.map((t) => t.id);
export const typeLabel = (id: string) => TYPES.find((t) => t.id === id)?.label ?? id;

export type Status =
  | 'new' | 'reviewed' | 'triage' | 'scoping' | 'building' | 'internal_test'
  | 'testing' | 'release' | 'adoption' | 'final_review' | 'completed';

export type StageGroup = 'intake' | 'development' | 'test_release' | 'closeout' | 'done';

export const STATUSES: { id: Status; label: string; group: StageGroup }[] = [
  { id: 'new', label: 'New', group: 'intake' },
  { id: 'reviewed', label: 'Reviewed', group: 'intake' },
  { id: 'triage', label: 'Researching / Triage', group: 'intake' },
  { id: 'scoping', label: 'Dev: Scoping', group: 'development' },
  { id: 'building', label: 'Dev: Building', group: 'development' },
  { id: 'internal_test', label: 'Dev: Internal test', group: 'development' },
  { id: 'testing', label: 'Testing', group: 'test_release' },
  { id: 'release', label: 'Release', group: 'test_release' },
  { id: 'adoption', label: 'Adoption / Close-out', group: 'closeout' },
  { id: 'final_review', label: 'Final review', group: 'closeout' },
  { id: 'completed', label: 'Completed', group: 'done' },
];
export const STATUS_IDS = STATUSES.map((s) => s.id);
export const statusLabel = (id: string) => STATUSES.find((s) => s.id === id)?.label ?? id;
export const statusIndex = (id: string) => STATUS_IDS.indexOf(id as Status);

export const STAGE_GROUPS: { id: StageGroup; label: string }[] = [
  { id: 'intake', label: 'Intake' },
  { id: 'development', label: 'Development' },
  { id: 'test_release', label: 'Test + Release' },
  { id: 'closeout', label: 'Close-out' },
  { id: 'done', label: 'Done' },
];

export type Source = 'form' | 'email' | 'quick' | 'claude' | 'import';
export const SOURCE_IDS: Source[] = ['form', 'email', 'quick', 'claude', 'import'];

export type PlannerSlot = 'none' | 'today' | 'tomorrow' | 'week';
export const PLANNER_SLOTS: PlannerSlot[] = ['none', 'today', 'tomorrow', 'week'];

/** Form urgency hint (the requester's words); Baker sets Impact x Urgency at review. */
export type UrgencyHint = 'blocking' | 'week' | 'whenever';
export const URGENCY_HINTS: { id: UrgencyHint; label: string }[] = [
  { id: 'blocking', label: "It's blocking me" },
  { id: 'week', label: 'This week' },
  { id: 'whenever', label: 'Whenever' },
];

/** Defaults; Admin can override in config/settings. */
export const DEFAULT_SETTINGS = {
  capacityDay: 6,
  capacityWeek: 30,
  tiers: { critical: 20, high: 13, medium: 7 },
};
export type Settings = typeof DEFAULT_SETTINGS;

export type Tier = 'critical' | 'high' | 'medium' | 'low' | 'unscored';
export function score(impact?: number | null, urgency?: number | null): number | null {
  return impact && urgency ? impact * urgency : null;
}
export function tier(s: number | null, t = DEFAULT_SETTINGS.tiers): Tier {
  if (s == null) return 'unscored';
  if (s >= t.critical) return 'critical';
  if (s >= t.high) return 'high';
  if (s >= t.medium) return 'medium';
  return 'low';
}
